import React, { useState, useMemo, useCallback, useContext } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { LoginContext } from "../../../../services/api/authentication-api";
import { clearDetailStateFromStorage, getAccordionRowRender, getFilteredSortedData, getInitialDetailState, PowerBankerNoDealOnFilterState, PowerStafferEmptyState, ProjectIdCell, saveDetailStateToStorage, useGroupToggle, renderAvatarWithName } from "../dashboard-pui-banker/power-banker-time-log/power-banker-helper";
import { getFlattenedData } from "../../dashboard-energy/dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log-helper";
import { createColumnMenu, renderTooltipHeader } from "../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import DealTeamAvatarGroupCell from "../../dashboard-util/dashboard-telerik-helper/deal-team-avatar";
import { FluentBadge } from "../../../../components/FluentUI/Fluent-Badge";
import { FluentTooltip } from "../../../../components/FluentUI/Fluent-Tooltip";
import "../dashboard-pui-banker/power-banker-time-log/power-banker-time-log.scss";
import DashboardPowerProjectTrackerHeader from "./dashboard-pui-project-tracker-header";
import PowerProjectTrackerModal from "./dashboard-pui-project-tracker-modal";
import PropTypes from "prop-types";

const DashboardPuiProjectTracker = ({
    projects = [],
    members = [],
}) => {
    const user = useContext(LoginContext);
    const [dealState, setDealState] = useState(() => getInitialDetailState(user?.userID, "powerProjectTracker"));
    const { groupState, toggleGroup, setGroupState } = useGroupToggle();
    const [show, setShow] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const flattenedData = useMemo(() =>
        getFlattenedData(projects), [projects]);
    const filteredData = useMemo(() =>
        getFilteredSortedData(flattenedData, dealState.filter, dealState.sort), [flattenedData, dealState.filter, dealState.sort]);
    const visibleData = useMemo(() => filteredData, [filteredData]);
    const columnMenu = createColumnMenu(flattenedData);
    const AccordionRowRender = useMemo(() => getAccordionRowRender(filteredData, groupState, toggleGroup, 11), [filteredData, groupState]);
    const isEmpty = visibleData?.length === 0;
    const finalGridData = isEmpty ? [{ isEmptyRow: true, showSearchEmptyState: false, isFilterEmptyState: (dealState.filter?.filters?.length ?? 0) > 0 }] : visibleData;

    const columnIsFiltered = useCallback(
        (fieldName) => isFilteredColumn(fieldName, dealState.filter),
        [dealState.filter]
    );

    const onHide = () => {
        setShow(false);
        setSelectedProject(null);
    };
    const onRowClick = (event) => {
        setSelectedProject(event.dataItem);
        setShow(true);
    };
    const resetDataState = () => {
        const resetState = { sort: [], filter: null };
        setDealState(resetState);
        clearDetailStateFromStorage(user?.userID, "powerProjectTracker");
    };

    return (
        <div className="power-banker-time-log power-staffer-widgets clickable-row dashboard-container-shadow">
            <DashboardPowerProjectTrackerHeader
                projects={projects}
                members={members}
                user={user}
                resetDataState={resetDataState}
                dealState={dealState}
            />
            <div className="detail-grid-scroll-container" data-testid="energy-grid">
                <Grid
                    data={finalGridData}
                    dataItemKey={"ProjectId"}
                    sortable={true}
                    sort={dealState.sort}
                    filter={dealState.filter}
                    onSortChange={(e) => {
                        setDealState({ ...dealState, sort: e.sort })
                        saveDetailStateToStorage(user.userID, { ...dealState, sort: e.sort }, "", "powerProjectTracker");
                        setGroupState({ ...groupState });
                    }}
                    onFilterChange={(e) => {
                        saveDetailStateToStorage(user.userID, { ...dealState, filter: e.filter }, "", "powerProjectTracker");
                        setDealState({ ...dealState, filter: e.filter })
                    }}
                    onRowClick={onRowClick}
                    rowRender={AccordionRowRender}
                    resizable={true}
                >
                    <Column
                        field="ProjectId"
                        title={renderTooltipHeader("#")}
                        filter="text"
                        columnMenu={columnMenu}
                        width={150}
                        headerClassName={columnIsFiltered("ProjectId") ? "filtered" : ""}
                        cell={(props) =>
                            projects?.length > 0
                                ? props.dataItem.isFilterEmptyState
                                    ? <td colSpan={11} className=" k-table-td ">
                                        <PowerBankerNoDealOnFilterState resetDataState={resetDataState} />
                                    </td>
                                    : <ProjectIdCell {...props} canEdit={user?.currentUser?.privileges?.["Projects"]?.Edit && props.dataItem.IsProjectMember} isNewDeal={props.dataItem.IsNewDeal} />
                                : <td colSpan={11}>
                                    <PowerStafferEmptyState canCreate={user?.currentUser?.privileges?.["Projects"]?.Add ?? false} />
                                </td>
                        }
                    />
                    <Column
                        field="ClientName"
                        title={renderTooltipHeader("Client Name")}
                        columnMenu={columnMenu}
                        width={200}
                        headerClassName={columnIsFiltered("ClientName") ? "filtered" : ""}
                        cell={(props) => (
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <FluentTooltip
                                target={<td className="background-white k-table-td"> {props.dataItem.ClientName}</td>}
                                text={props.dataItem.ClientName}>
                            </FluentTooltip>
                        )}
                    />
                    <Column
                        field="ProjectName"
                        title={renderTooltipHeader("Project Name")}
                        columnMenu={columnMenu}
                        width={200}
                        headerClassName={columnIsFiltered("ProjectName") ? "filtered" : ""}
                        cell={(props) => (
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <FluentTooltip
                                target={<td data-testid="project-name-column" className="background-white k-table-td"> {props.dataItem.ProjectName}{props.dataItem.IsOverdue}   {" "}
                                    {props.dataItem.IsOverdue && (<FluentBadge
                                        appearance="filled"
                                        size="small"
                                        color="warning"
                                        text="Overdue"
                                        shape="rounded"
                                    />)}
                                </td>}
                                text={props.dataItem.ProjectName}>
                            </FluentTooltip>
                        )}
                    />
                    <Column
                        field="ProjectType"
                        title={renderTooltipHeader("Project Type")}
                        columnMenu={columnMenu}
                        width={200}
                        headerClassName={columnIsFiltered("ProjectType") ? "filtered" : ""}
                        cell={(props) => (
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <FluentTooltip
                                target={<td className="background-white k-table-td"> {props.dataItem.ProjectType}</td>}
                                text={props.dataItem.ProjectType}>
                            </FluentTooltip>
                        )}
                    />
                    <Column
                        field="TotalWeekHrs"
                        title={renderTooltipHeader("Total Wk Hrs")}
                        columnMenu={columnMenu}
                        width={160}
                        headerClassName={columnIsFiltered("TotalWeekHrs") ? "filtered" : ""}
                        cell={(props) => (
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <td className="summary-cell k-table-td" >
                                {props.dataItem.TotalWeekHrs ?? "—"}
                            </td>
                        )}
                    />
                    <Column
                        field="TotalWeekendHrs"
                        title={renderTooltipHeader("Total Wknd Hrs")}
                        columnMenu={columnMenu}
                        width={160}
                        headerClassName={columnIsFiltered("TotalWeekendHrs") ? "filtered" : ""}
                        cell={(props) => (
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <td className="summary-cell k-table-td" >
                                {props.dataItem.TotalWeekendHrs ?? "—"}
                            </td>
                        )}
                    />
                    <Column
                        field="Members"
                        title={renderTooltipHeader("Project Team")}
                        filter="text"
                        columnMenu={columnMenu}
                        width={150}
                        headerClassName={columnIsFiltered("Members") ? "filtered" : ""}
                        cell={(props) =>
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="background-white k-table-td" >
                                {props.dataItem.Members?.length
                                    ? <DealTeamAvatarGroupCell members={props.dataItem.Members} />
                                    : "-"
                                }
                            </td>
                        }
                    />
                    <Column
                        field="CoverageSenior"
                        title={renderTooltipHeader("Coverage Senior")}
                        columnMenu={columnMenu}
                        width={170}
                        headerClassName={columnIsFiltered("CoverageSenior") ? "filtered" : ""}
                        cell={(props) => (
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="summary-cell k-table-td" >
                                {renderAvatarWithName(props.dataItem.CoverageSenior, "teal")}
                            </td>
                        )}
                    />
                    <Column
                        field="ProductSenior"
                        title={renderTooltipHeader("Product Senior")}
                        columnMenu={columnMenu}
                        width={170}
                        headerClassName={columnIsFiltered("ProductSenior") ? "filtered" : ""}
                        cell={(props) => (
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="summary-cell k-table-td" >
                                {renderAvatarWithName(props.dataItem.ProductSenior, "lavender")}
                            </td>
                        )}
                    />
                    <Column
                        field="CoverageVP"
                        title={renderTooltipHeader("Coverage VP")}
                        columnMenu={columnMenu}
                        width={170}
                        headerClassName={columnIsFiltered("CoverageVP") ? "filtered" : ""}
                        cell={(props) => (
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="summary-cell k-table-td" >
                                {renderAvatarWithName(props.dataItem.CoverageVP, "lilac")}
                            </td>
                        )}
                    />
                    <Column
                        field="CoverageDirector"
                        title={renderTooltipHeader("Coverage Director")}
                        columnMenu={columnMenu}
                        width={180}
                        headerClassName={columnIsFiltered("CoverageDirector") ? "filtered" : ""}
                        cell={(props) => (
                            (projects?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="summary-cell k-table-td" >
                                {renderAvatarWithName(props.dataItem.CoverageDirector, "red")}
                            </td>
                        )}
                    />
                </Grid>
            </div>
            {show && (
                <PowerProjectTrackerModal
                    show={show}
                    onHide={onHide}
                    members={selectedProject}
                    user={user}
                />
            )}
        </div>
    );
};

DashboardPuiProjectTracker.propTypes = {
    projects: PropTypes.array,
    user: PropTypes.object,
    resetDataState: PropTypes.func,
    dealState: PropTypes.object,
    members: PropTypes.array,
};

export default DashboardPuiProjectTracker;
